import React from "react";
import { fmtDay, fmtRange } from "../rates/rateModel";
import { amt, CORRECTION_TITLE, correctionBalance, correctionKind, correctionLeft, correctionWhat, dayHead, L, notForPaymentLabel } from "./paySheetModel";

const Bi = ({ l }: { l: readonly [string, string] }) => (
  <>
    {l[0]}
    <span className="kps-hi">{l[1]}</span>
  </>
);

/**
 * The paper the workers sign: studio name, the chosen dates as columns, one row per person with each
 * day's work and amount, total, earlier due, advance cut, net to pay and a signature box.
 */
const PaySheetPrint = ({ sheet }: { sheet: any }) => {
  const dates: string[] = sheet.dates || [];
  const lines: any[] = sheet.lines || [];
  return (
    <div className="kps-print">
      <header className="kps-head">
        <div>
          <h1>{sheet.orgName || "Karkhana"}</h1>
          <div className="kps-title">
            <Bi l={L.title} />
          </div>
          {notForPaymentLabel(sheet) ? (
            // A draft (or discarded draft) printed for checking must never pass for the sheet people sign when paid.
            <div className="kps-title">
              <strong>
                <Bi l={notForPaymentLabel(sheet)!} />
              </strong>
            </div>
          ) : null}
        </div>
        <div className="kps-meta">
          <div>
            <strong>
              <Bi l={L.dates} />
            </strong>{" "}
            {dayHead(sheet.from).day} – {dayHead(sheet.to).day}/{sheet.to.slice(0, 4)}
          </div>
          <div className="kps-small">
            {L.printed[0]} / {L.printed[1]}: {new Date().toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
          </div>
        </div>
      </header>

      <table className="kps-table">
        <thead>
          <tr>
            <th className="kps-code">
              <Bi l={L.code} />
            </th>
            <th className="kps-name">
              <Bi l={L.name} />
            </th>
            {dates.map((d) => {
              const h = dayHead(d);
              return (
                <th key={d} className="kps-day">
                  {h.en} {h.day}
                  <span className="kps-hi">{h.hi}</span>
                </th>
              );
            })}
            <th className="kps-num">
              <Bi l={L.total} />
            </th>
            <th className="kps-num">
              <Bi l={L.earlier} />
            </th>
            <th className="kps-num">
              <Bi l={L.cut} />
            </th>
            <th className="kps-num kps-net">
              <Bi l={L.net} />
            </th>
            <th className="kps-sign">
              <Bi l={L.sign} />
            </th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => (
            <tr key={l.personId}>
              <td className="kps-code">{l.code}</td>
              <td className="kps-name">
                {l.name}
                {Number(l.advanceOwed) > 0 ? (
                  <span className="kps-small">
                    {L.advance[0]} / {L.advance[1]}: {amt(l.advanceOwed)}
                  </span>
                ) : null}
                {(l.corrections || []).map((c: any, i: number) => {
                  const k = CORRECTION_TITLE[correctionKind(c)];
                  return (
                    <span key={c.key || c.entryId || i} className="kps-small">
                      {L.correction[0]} / {L.correction[1]} · {k[1]} / {k[2]}: {correctionWhat(c, fmtDay, fmtRange)} {amt(c.amount)}
                      {correctionLeft(c) != null ? ` (To recover / वसूली बाकी: ${amt(correctionLeft(c))})` : ""}
                    </span>
                  );
                })}
                {correctionBalance(l).outstanding ? (
                  <span className="kps-small">
                    {L.toRecover[0]} / {L.toRecover[1]}: {amt(correctionBalance(l).outstanding)} · {L.taken[0]} / {L.taken[1]}:{" "}
                    {amt(correctionBalance(l).taken) || "₹0.00"}
                    {correctionBalance(l).carried ? ` · ${L.carried[0]} / ${L.carried[1]}: ${amt(correctionBalance(l).carried)}` : ""}
                  </span>
                ) : null}
                {l.warning ? <span className="kps-small">{l.warning}</span> : null}
              </td>
              {l.days.map((c: any) => (
                <td key={c.date} className="kps-day">
                  {(c.work || []).map((w: string, i: number) => (
                    <span key={i} className="kps-work">
                      {w}
                    </span>
                  ))}
                  {c.note ? (
                    <span className="kps-small">{c.note === "IN_PROGRESS" ? `${L.stillIn[0]} / ${L.stillIn[1]}` : `${L.notCounted[0]} / ${L.notCounted[1]}`}</span>
                  ) : null}
                  <span className="kps-amt">{amt(c.amount)}</span>
                </td>
              ))}
              <td className="kps-num">{amt(l.periodEarned)}</td>
              <td className="kps-num">
                {Number(l.earlierDue) > 0 ? amt(l.earlierDue) : null}
                {Number(l.earlierDue) < 0 ? (
                  <span className="kps-small">
                    {L.alreadyPaid[0]} / {L.alreadyPaid[1]} {amt(-Number(l.earlierDue))}
                  </span>
                ) : null}
              </td>
              <td className="kps-num">{amt(l.paid ? l.paidCut : l.cut)}</td>
              <td className="kps-num kps-net">{amt(l.paid ? l.paidAmount : l.netDue) || "₹0.00"}</td>
              <td className="kps-sign">
                {l.paid ? (
                  <span className="kps-small">
                    {L.paid[0]} / {L.paid[1]}
                  </span>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={2 + dates.length}>
              <Bi l={L.sheetTotal} />
            </td>
            <td className="kps-num">{amt(sheet.totals?.periodEarned)}</td>
            <td />
            <td className="kps-num">{amt(sheet.totals?.cut)}</td>
            <td className="kps-num kps-net">{amt(sheet.totals?.netDue)}</td>
            <td />
          </tr>
        </tfoot>
      </table>

      <footer className="kps-foot">
        <div>
          <Bi l={L.supervisor} />: {sheet.createdByName}
        </div>
        <div className="kps-signline">
          <Bi l={L.supervisor} /> — <Bi l={L.sign} />
        </div>
      </footer>
    </div>
  );
};

export default PaySheetPrint;
